/**
 * /crm/refunds — refund decision queue, with the accounts settlement alongside.
 *
 * A refund lives in two places: `CRMRefundRequest` holds the CRM decision, and
 * `payment.Refund` holds the money actually moving. Accounts settle from the
 * Django HTML screens, which never link the two records, so this page resolves
 * the settlement by order id instead of trusting the foreign key. Without that,
 * every refund paid out from the HTML page looked unsettled here.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Filter, Receipt, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tooltip } from '@/components/ui/Tooltip';
import { CrmRefundDetailDialog } from '@/components/crm/CrmRefundDetailDialog';
import {
  useCrmRefunds,
  type CrmRefund,
  type CrmRefundDecision,
  type CrmSettlementFilter,
} from '@/api/bi/crm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { REFUND_DECISION_TONE } from '@/lib/crmStatus';
import { fmt } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { downloadCsv } from '@/components/data/DataView';

const FILTERS: Array<{ value: CrmRefundDecision | 'all'; label: string }> = [
  { value: 'all', label: 'All decisions' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const SETTLEMENT_FILTERS: Array<{ value: CrmSettlementFilter | 'all'; label: string }> = [
  { value: 'all', label: 'Any settlement' },
  { value: 'completed', label: 'Settlement completed' },
  { value: 'pending', label: 'Settlement pending' },
  { value: 'cancelled', label: 'Settlement cancelled' },
  { value: 'settled', label: 'Settled (any outcome)' },
  { value: 'unsettled', label: 'No settlement record' },
];

const SETTLEMENT_TONE = {
  completed: 'success',
  pending: 'warning',
  cancelled: 'neutral',
} as const;

/**
 * Accounts have paid the customer but CRM never recorded an approval. This is
 * exactly what HTML-only processing produces, and it is invisible on the
 * decision column alone.
 */
function isDiscrepant(r: CrmRefund): boolean {
  return r.settlement?.status === 'completed' && r.decision !== 'approved';
}

function SettlementCell({ refund }: { refund: CrmRefund }) {
  const s = refund.settlement;
  if (!s) {
    return <span className="text-muted-foreground">Not settled</span>;
  }
  return (
    <div>
      <div className="flex items-center gap-1">
        <Badge tone={SETTLEMENT_TONE[s.status]}>{s.status}</Badge>
        {refund.settlement_source === 'matched_by_order' ? (
          <Tooltip content="Matched by order id — this settlement was recorded from the accounts HTML page, which does not link back to the CRM request.">
            <span className="cursor-default text-[10px] text-muted-foreground">
              matched
            </span>
          </Tooltip>
        ) : null}
      </div>
      <div className="text-[10px] text-muted-foreground">
        {fmt.npr(s.refund_amount)}
        {s.completed_at ? ` · ${fmt.datetime(s.completed_at)}` : ''}
      </div>
    </div>
  );
}

export function CrmRefundsPage() {
  const [decision, setDecision] = useState<CrmRefundDecision | 'all'>('pending');
  const [settlement, setSettlement] = useState<CrmSettlementFilter | 'all'>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [openRefund, setOpenRefund] = useState<CrmRefund | null>(null);
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  const refunds = useCrmRefunds({
    decision: decision === 'all' ? null : decision,
    settlementStatus: settlement === 'all' ? null : settlement,
    q: debouncedQ || null,
    page,
    pageSize: 25,
  });
  const rows = refunds.data?.results ?? [];
  const pagination = refunds.data?.pagination;
  // A page is 25 rows at most, so counting on every render is cheaper than the
  // memo bookkeeping would be.
  const discrepancies = rows.filter(isDiscrepant).length;

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Refunds"
        description="Refund records waiting for approval, plus historical decisions. Approve here, then accounts posts the credit note — the settlement column shows what accounts actually did."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 text-xs">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search order, ticket, customer…"
            className="h-8 w-64 border-0 px-1 text-xs focus-visible:ring-0"
          />
        </div>
        <Select
          value={decision}
          onValueChange={(v) => {
            setDecision(v as never);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-44 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={settlement}
          onValueChange={(v) => {
            setSettlement(v as never);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-52 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SETTLEMENT_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">
          {pagination
            ? `${pagination.total.toLocaleString()} refund${pagination.total === 1 ? '' : 's'}`
            : ''}
        </div>
        <Button variant="outline" size="sm" onClick={()=>{
         downloadCsv('crm-refunds', rows.map(toCsvRow), CSV_HEADERS)
        }}>
                        <Download className="h-3.5 w-3.5" aria-hidden />
                        CSV
                      </Button>
      </div>

      {discrepancies > 0 ? (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-soft">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <strong>
              {discrepancies} refund{discrepancies === 1 ? '' : 's'} on this page{' '}
              {discrepancies === 1 ? 'has' : 'have'} been paid out without a CRM
              approval.
            </strong>{' '}
            Accounts completed the settlement while the CRM decision is still pending
            or rejected — the signature of a refund processed straight from the
            accounts page. Those tickets stay open and the money keeps counting as
            leakage until the decision is recorded here.
          </div>
        </div>
      ) : null}

      <Card>
        <CardBody className="p-0">
          {refunds.isLoading ? (
            <div className="space-y-2 px-5 py-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : refunds.isError ? (
            <div className="px-5 py-4">
              <ErrorState
                description="Could not load the refund queue."
                onRetry={() => void refunds.refetch()}
              />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Receipt className="h-5 w-5" />}
              title="No refunds in this view"
              description={
                debouncedQ
                  ? 'No refunds match this search. Try another order, ticket, or customer.'
                  : 'Adjust the filters or wait for the next refund call.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Refund</th>
                    <th className="px-4 py-2 text-left">Customer</th>
                    <th className="px-4 py-2 text-left">Order</th>
                    <th className="px-4 py-2 text-left">Decision</th>
                    <th className="px-4 py-2 text-left">Settlement</th>
                    <th className="px-4 py-2 text-right">Items</th>
                    <th className="px-4 py-2 text-right">Requested</th>
                    <th className="px-4 py-2 text-right">Approved</th>
                    <th className="px-4 py-2 text-right">Decided</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenRefund(r)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenRefund(r);
                        }
                      }}
                      className="cursor-pointer border-t border-border transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
                    >
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/crm/tickets/${r.ticket_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium hover:underline"
                          >
                            Ticket #{r.ticket_id}
                          </Link>
                          {isDiscrepant(r) ? (
                            <Tooltip content="Paid out by accounts without a CRM approval.">
                              <TriangleAlert className="h-3.5 w-3.5 text-warning" />
                            </Tooltip>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-2">{r.customer?.name ?? '—'}</td>
                      <td className="px-4 py-2">
                        {r.order_id != null ? (
                          <Link
                            to={`/orders/${r.order_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium text-primary hover:underline"
                          >
                            #{r.order_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Badge tone={REFUND_DECISION_TONE[r.decision]}>{r.decision}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        <SettlementCell refund={r} />
                      </td>
                      <td className="px-4 py-2 text-right">{r.items.length}</td>
                      <td className="px-4 py-2 text-right">{fmt.npr(r.requested_amount)}</td>
                      <td className="px-4 py-2 text-right">
                        {r.approved_amount != null ? fmt.npr(r.approved_amount) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-muted-foreground">
                        {r.decided_at ? fmt.ago(r.decided_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {pagination && pagination.total_pages > 1 ? (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pagination.page <= 1}
            className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            {pagination.page} / {pagination.total_pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            disabled={pagination.page >= pagination.total_pages}
            className="press rounded-md border border-border px-2 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      <CrmRefundDetailDialog
        refund={openRefund}
        onClose={() => setOpenRefund(null)}
      />
    </>
  );
}


const CSV_HEADERS = [
  { key: 'id' as const, label: 'Refund' },
  // { key: 'type' as const, label: 'Type' },
  // { key: 'summary' as const, label: 'Summary' },
  // { key: 'status' as const, label: 'Status' },
  // { key: 'priority' as const, label: 'Priority' },
  // { key: 'owner' as const, label: 'Owner' },
  // { key: 'sla_stage' as const, label: 'SLA stage' },
  // { key: 'order_id' as const, label: 'Order' },
  // { key: 'store' as const, label: 'Store' },
  { key: 'customer' as const, label: 'Customer' },
  { key: 'order' as const, label: 'Order' },
  { key: 'settlement' as const, label: 'Settlement' },
  { key: 'items' as const, label: 'Items' },
  { key: 'requested_amount' as const, label: 'Requested' },
  { key: 'approved' as const, label: 'Approved' },
  { key: 'decided_at' as const, label: 'Decided' },
  { key: 'created_at' as const, label: 'Created' },
  { key: 'updated_at' as const, label: 'Updated' },

];

function toCsvRow(refund: CrmRefund) {
  return {
    id: refund.id,
    // type: Refund_TYPE_LABEL[refund.type],
    customer: refund.customer?.name,
    order: refund.order_id,
    decision: refund.decision,
    settlement: refund.settlement?.status || "Not settled",
    items: refund.items?.length,
    requested_amount:refund.requested_amount,
    approved: refund.approved_amount != null? "Approved":"__",
    decided_at: refund.decided_at || "__",
    created_at: refund.created_at,
    updated_at: refund.updated_at,
  };
}
